import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import BrandHeader from "../components/BrandHeader";
import Particles from "../components/Particles";
import { customerApi } from "../lib/api";
import { saveSession, getSession } from "../lib/customerSession";
import { useSEO } from "../lib/seo";

function digitsOnly(v) {
  return (v || "").replace(/\D/g, "");
}

export default function MyPrescriptionLogin() {
  useSEO({
    title: "My Prescription — Login | Pancha Healing",
    description: "Access your personalised crystal prescription and monthly reports.",
    noIndex: true,
  });
  const navigate = useNavigate();
  const existing = getSession();
  // If already logged in, go straight to the dashboard.
  if (existing?.orderId) {
    setTimeout(() => navigate("/my-prescription/dashboard", { replace: true }), 0);
  }

  const [mobile, setMobile] = useState("");
  const [dob, setDob] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = mobile.length >= 10 && dob.length === 10 && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await customerApi.login({ mobile, dob });
      saveSession({
        orderId: res.orderId,
        fullName: res.fullName,
        orderNumber: res.orderNumber,
        loggedInAt: new Date().toISOString(),
      });
      toast.success(`Welcome back, ${(res.fullName || "").split(" ")[0]}`);
      navigate("/my-prescription/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Login failed";
      setError(detail);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="my-prescription-login">
      <BrandHeader />
      <section className="relative overflow-hidden hero-mystical">
        <Particles variant="violet" />
        <div className="relative max-w-md mx-auto px-5 md:px-8 py-14 md:py-20 text-center">
          <div className="ph-overline" style={{ color: "#D4A855" }}>Customer Portal</div>
          <h1
            className="font-serif text-3xl md:text-4xl mt-3 text-white"
            data-testid="login-headline"
          >
            Open Your Sacred Prescription
          </h1>
          <p
            className="font-serif italic text-base mt-4"
            style={{ color: "#FAF7F2" }}
          >
            Enter the mobile number and date of birth you used at checkout.
          </p>
        </div>
      </section>

      <section className="bg-[#FAF7F2] py-10 md:py-14">
        <div className="max-w-md mx-auto px-5 md:px-8">
          <form
            onSubmit={handleSubmit}
            className="ph-card p-6 md:p-8 space-y-5"
            data-testid="login-form"
          >
            <Field label="Mobile number (10 digits, India)">
              <div className="flex items-stretch">
                <span
                  className="inline-flex items-center px-3 rounded-l-md text-sm font-medium"
                  style={{
                    background: "#EEEDFE",
                    color: "#2C0A5E",
                    border: "1px solid rgba(106,13,173,0.18)",
                    borderRight: "none",
                  }}
                >
                  +91
                </span>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  className="ph-input rounded-l-none"
                  value={mobile}
                  onChange={(e) => setMobile(digitsOnly(e.target.value).slice(0, 10))}
                  placeholder="98765 43210"
                  data-testid="login-mobile"
                  autoComplete="tel-national"
                  required
                />
              </div>
            </Field>

            <Field label="Date of birth">
              <input
                type="date"
                className="ph-input"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                data-testid="login-dob"
                autoComplete="bday"
                required
              />
            </Field>

            {error && (
              <p
                className="text-sm text-center"
                style={{ color: "#C13B1B" }}
                data-testid="login-error"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="login-submit"
              style={{
                width: "100%",
                padding: "16px 24px",
                borderRadius: "12px",
                background: canSubmit ? "#6A0DAD" : "#B7A6CD",
                color: "#D4A855",
                fontWeight: 700,
                letterSpacing: "0.04em",
                fontSize: "16px",
                cursor: canSubmit ? "pointer" : "not-allowed",
                transition: "background-color 200ms ease",
                boxShadow: canSubmit
                  ? "0 8px 24px rgba(106,13,173,0.35)"
                  : "none",
              }}
            >
              {submitting ? "Verifying…" : "Open My Dashboard →"}
            </button>

            <p className="text-xs text-center text-[#6B5E7E]">
              Trouble logging in? WhatsApp us — we&apos;ll help in under an hour.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[#2C0A5E]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
